import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";

export type FormValuesKey = keyof IntegrationFormData;

interface ZendeskFormProps {
  serverErrors?: ServerErrors;
}

const PRIORITY_OPTIONS = [
  {value: "", label: "Default (none)"},
  {value: "low", label: "Low"},
  {value: "normal", label: "Normal"},
  {value: "high", label: "High"},
  {value: "urgent", label: "Urgent"},
];

const TYPE_OPTIONS = [
  {value: "", label: "Default (none)"},
  {value: "problem", label: "Problem"},
  {value: "incident", label: "Incident"},
  {value: "question", label: "Question"},
  {value: "task", label: "Task"},
];

export const ZendeskForm = ({serverErrors}: ZendeskFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_email"
            label="API Email Address"
            required={true}
            tooltip="Email of the Zendesk agent whose API token is being used."
            autoFocus={true}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_token"
            label="API Token"
            required={true}
            tooltip="API token created for the agent in the Zendesk Admin Center."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.zendesk_subdomain"
            label="Zendesk Subdomain"
            required={true}
            tooltip='The base URL of your Zendesk account, e.g. "https://your-company.zendesk.com"'
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.tags"
            label="Tags"
            required={false}
            tooltip='A comma separated list of tags to attach to each ticket created. The "Uptime.com" tag and any associated Check tags are always set.'
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <SelectField
            name="settings.priority"
            label="Ticket Priority"
            required={false}
            options={PRIORITY_OPTIONS}
            tooltip="Priority to assign to tickets created by this integration."
          />
        </div>
        <div className="col-6 mb-3">
          <SelectField
            name="settings.ticket_type"
            label="Ticket Type"
            required={false}
            options={TYPE_OPTIONS}
            tooltip="Type to assign to tickets created by this integration."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-12 mb-3">
          <SwitchField
            name="settings.autoresolve"
            label="Auto-solve Ticket"
            tooltip="Automatically mark the ticket as solved once the Check is back up."
          />
        </div>
      </div>
      <SetupInfo>
        <div className="p-2">
          This integration creates a ticket in your Zendesk account when an Alert is raised, and marks the ticket as
          solved when the Alert is cleared.
        </div>
        <div className="p-2">
          <ol className="px-3">
            <li>
              <p className="mb-0 pulseTextBodyBold">Generate a Zendesk API Token</p>
              <p className="mb-1">
                In Zendesk, go to{" "}
                <span className="pulseTextBodyBold">
                  Admin Center &gt; Apps and integrations &gt; APIs &gt; API Tokens
                </span>
                , enable Token Access, and create a new API token. Copy the token into the{" "}
                <span className="pulseTextBodyBold">API Token</span> field above. The{" "}
                <span className="pulseTextBodyBold">API Email Address</span> must belong to a Zendesk agent with
                permission to create tickets.
              </p>
              <p className="mb-1">
                <span className="pulseTextBodyBold">Note:</span> In the same{" "}
                <span className="pulseTextBodyBold">Apps and integrations</span> screen, under{" "}
                <span className="pulseTextBodyBold">API Configuration</span>, make sure{" "}
                <span className="pulseTextBodyBold">Allow API token access</span> is enabled.
              </p>
            </li>
            <li>
              <p className="mb-0 pulseTextBodyBold">Determine your Zendesk Subdomain</p>
              <p className="mb-1">
                Enter the base URL of your Zendesk account into the{" "}
                <span className="pulseTextBodyBold">Zendesk Subdomain</span> field, e.g.{" "}
                <code>https://your-company.zendesk.com</code>.
              </p>
            </li>
          </ol>
        </div>
      </SetupInfo>
    </>
  );
};
